import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { ScrollArea } from '../ui/scroll-area';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import { 
  AlertTriangle, 
  Bug, 
  Shield, 
  MessageSquare,
  Clock,
  CheckCircle,
  XCircle,
  Loader2,
  RefreshCw,
  Eye,
  Send,
  Image as ImageIcon,
  MessageCircle,
  User,
  Calendar,
  Filter
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const REPORT_TYPES = {
  system_error: { label: 'System Error', icon: AlertTriangle, color: 'bg-red-500' },
  spam_report: { label: 'Spam Report', icon: Shield, color: 'bg-orange-500' },
  bug: { label: 'Bug', icon: Bug, color: 'bg-yellow-500' },
  feature_request: { label: 'Feature', icon: MessageSquare, color: 'bg-blue-500' },
  other: { label: 'Other', icon: MessageSquare, color: 'bg-gray-500' }
};

const STATUS_OPTIONS = {
  open: { label: 'Open', color: 'bg-blue-500' },
  in_progress: { label: 'In Progress', color: 'bg-yellow-500' },
  resolved: { label: 'Resolved', color: 'bg-green-500' },
  closed: { label: 'Closed', color: 'bg-gray-500' }
};

const PRIORITY_OPTIONS = {
  low: { label: 'Low', color: 'bg-gray-500' },
  medium: { label: 'Medium', color: 'bg-blue-500' },
  high: { label: 'High', color: 'bg-orange-500' },
  critical: { label: 'Critical', color: 'bg-red-500' }
};

export default function SupportManager() {
  const [reports, setReports] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedReport, setSelectedReport] = useState(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [replyDialogOpen, setReplyDialogOpen] = useState(false);
  const [replyMessage, setReplyMessage] = useState('');
  const [newComment, setNewComment] = useState('');
  const [isInternalComment, setIsInternalComment] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  // Filters
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');

  const fetchReports = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.append('status_filter', statusFilter);
      if (typeFilter) params.append('type_filter', typeFilter);
      if (priorityFilter) params.append('priority_filter', priorityFilter);
      
      const [reportsRes, statsRes] = await Promise.all([
        axios.get(`${API}/support/reports?${params}`),
        axios.get(`${API}/support/stats`)
      ]);
      
      setReports(reportsRes.data);
      setStats(statsRes.data);
    } catch (err) {
      toast.error('Failed to load support reports');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReports();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, typeFilter, priorityFilter]);

  const handleViewReport = async (report) => {
    try {
      const response = await axios.get(`${API}/support/reports/${report.id}`);
      setSelectedReport(response.data);
      setViewDialogOpen(true);
    } catch (err) {
      toast.error('Failed to load report details');
    }
  };

  const handleUpdateReport = async (reportId, updates) => {
    try {
      await axios.patch(`${API}/support/reports/${reportId}`, updates);
      toast.success('Report updated');
      fetchReports();
      
      // Update selected report if open
      if (selectedReport?.id === reportId) {
        setSelectedReport({ ...selectedReport, ...updates });
      }
    } catch (err) {
      toast.error('Failed to update report');
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim() || !selectedReport) return;
    
    setSubmitting(true);
    try {
      await axios.post(`${API}/support/reports/${selectedReport.id}/comments`, {
        comment: newComment,
        is_internal: isInternalComment
      });
      toast.success('Comment added');
      setNewComment('');
      
      // Refresh report
      const response = await axios.get(`${API}/support/reports/${selectedReport.id}`);
      setSelectedReport(response.data);
    } catch (err) {
      toast.error('Failed to add comment');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSendReply = async () => {
    if (!replyMessage.trim() || !selectedReport) return;
    
    setSubmitting(true);
    try {
      await axios.post(`${API}/support/reports/${selectedReport.id}/reply`, {
        message: replyMessage
      });
      toast.success('Reply sent to user');
      setReplyMessage('');
      setReplyDialogOpen(false);
      
      // Refresh report
      const response = await axios.get(`${API}/support/reports/${selectedReport.id}`);
      setSelectedReport(response.data);
    } catch (err) {
      toast.error('Failed to send reply');
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleString();
  };

  const getTypeInfo = (type) => REPORT_TYPES[type] || REPORT_TYPES.other;
  const getStatusInfo = (status) => STATUS_OPTIONS[status] || STATUS_OPTIONS.open;
  const getPriorityInfo = (priority) => PRIORITY_OPTIONS[priority] || PRIORITY_OPTIONS.medium;

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="bg-[#1a1a2e] border-[#2d2d44]">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-500/20 rounded-lg">
                  <MessageCircle className="w-5 h-5 text-blue-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{stats.open}</p>
                  <p className="text-xs text-gray-400">Open</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-[#1a1a2e] border-[#2d2d44]">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-yellow-500/20 rounded-lg">
                  <Clock className="w-5 h-5 text-yellow-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{stats.in_progress}</p>
                  <p className="text-xs text-gray-400">In Progress</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-[#1a1a2e] border-[#2d2d44]">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-red-500/20 rounded-lg">
                  <AlertTriangle className="w-5 h-5 text-red-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{stats.urgent?.critical || 0}</p>
                  <p className="text-xs text-gray-400">Critical</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-[#1a1a2e] border-[#2d2d44]">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-500/20 rounded-lg">
                  <CheckCircle className="w-5 h-5 text-green-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{stats.resolved}</p>
                  <p className="text-xs text-gray-400">Resolved</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Reports Table */}
      <Card className="bg-[#1a1a2e] border-[#2d2d44]">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-white">Support Reports</CardTitle>
              <CardDescription className="text-gray-400">
                Manage user reports and feedback
              </CardDescription>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={fetchReports}
              className="text-gray-400 hover:text-white"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
          
          {/* Filters */}
          <div className="flex flex-wrap gap-3 mt-4">
            <Select value={statusFilter || 'all'} onValueChange={(v) => setStatusFilter(v === 'all' ? '' : v)}>
              <SelectTrigger className="w-[140px] bg-[#252540] border-[#2d2d44] text-white">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent className="bg-[#1a1a2e] border-[#2d2d44]">
                <SelectItem value="all" className="text-white">All Status</SelectItem>
                {Object.entries(STATUS_OPTIONS).map(([key, val]) => (
                  <SelectItem key={key} value={key} className="text-white">{val.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Select value={typeFilter || 'all'} onValueChange={(v) => setTypeFilter(v === 'all' ? '' : v)}>
              <SelectTrigger className="w-[140px] bg-[#252540] border-[#2d2d44] text-white">
                <SelectValue placeholder="Type" />
              </SelectTrigger>
              <SelectContent className="bg-[#1a1a2e] border-[#2d2d44]">
                <SelectItem value="all" className="text-white">All Types</SelectItem>
                {Object.entries(REPORT_TYPES).map(([key, val]) => (
                  <SelectItem key={key} value={key} className="text-white">{val.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Select value={priorityFilter || 'all'} onValueChange={(v) => setPriorityFilter(v === 'all' ? '' : v)}>
              <SelectTrigger className="w-[140px] bg-[#252540] border-[#2d2d44] text-white">
                <SelectValue placeholder="Priority" />
              </SelectTrigger>
              <SelectContent className="bg-[#1a1a2e] border-[#2d2d44]">
                <SelectItem value="all" className="text-white">All Priority</SelectItem>
                {Object.entries(PRIORITY_OPTIONS).map(([key, val]) => (
                  <SelectItem key={key} value={key} className="text-white">{val.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 text-[#6D4AFF] animate-spin" />
            </div>
          ) : reports.length === 0 ? (
            <div className="text-center py-12">
              <MessageCircle className="w-12 h-12 text-gray-600 mx-auto mb-4" />
              <p className="text-gray-400">No reports found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-[#2d2d44]">
                    <TableHead className="text-gray-400">Type</TableHead>
                    <TableHead className="text-gray-400">Subject</TableHead>
                    <TableHead className="text-gray-400">Reporter</TableHead>
                    <TableHead className="text-gray-400">Status</TableHead>
                    <TableHead className="text-gray-400">Priority</TableHead>
                    <TableHead className="text-gray-400">Date</TableHead>
                    <TableHead className="text-gray-400 text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {reports.map((report) => {
                    const typeInfo = getTypeInfo(report.report_type);
                    const statusInfo = getStatusInfo(report.status);
                    const priorityInfo = getPriorityInfo(report.priority);
                    const TypeIcon = typeInfo.icon;
                    
                    return (
                      <TableRow key={report.id} className="border-[#2d2d44] hover:bg-[#252540]">
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <div className={`p-1.5 rounded ${typeInfo.color}/20`}>
                              <TypeIcon className={`w-4 h-4 ${typeInfo.color.replace('bg-', 'text-')}`} />
                            </div>
                            <span className="text-gray-300 text-sm">{typeInfo.label}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <p className="text-white font-medium truncate max-w-[200px]">
                            {report.subject}
                          </p>
                        </TableCell>
                        <TableCell>
                          <p className="text-gray-400 text-sm">{report.reporter_email}</p>
                        </TableCell>
                        <TableCell>
                          <Select 
                            value={report.status} 
                            onValueChange={(v) => handleUpdateReport(report.id, { report_status: v })}
                          >
                            <SelectTrigger className="w-[120px] h-8 bg-transparent border-0 text-white">
                              <Badge className={`${statusInfo.color} text-white`}>
                                {statusInfo.label}
                              </Badge>
                            </SelectTrigger>
                            <SelectContent className="bg-[#1a1a2e] border-[#2d2d44]">
                              {Object.entries(STATUS_OPTIONS).map(([key, val]) => (
                                <SelectItem key={key} value={key} className="text-white">
                                  {val.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </TableCell>
                        <TableCell>
                          <Select 
                            value={report.priority} 
                            onValueChange={(v) => handleUpdateReport(report.id, { priority: v })}
                          >
                            <SelectTrigger className="w-[100px] h-8 bg-transparent border-0 text-white">
                              <Badge className={`${priorityInfo.color} text-white`}>
                                {priorityInfo.label}
                              </Badge>
                            </SelectTrigger>
                            <SelectContent className="bg-[#1a1a2e] border-[#2d2d44]">
                              {Object.entries(PRIORITY_OPTIONS).map(([key, val]) => (
                                <SelectItem key={key} value={key} className="text-white">
                                  {val.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </TableCell>
                        <TableCell className="text-gray-400 text-sm">
                          {new Date(report.created_at).toLocaleDateString()}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleViewReport(report)}
                            className="text-[#6D4AFF] hover:text-white"
                          >
                            <Eye className="w-4 h-4 mr-1" />
                            View
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* View Report Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="bg-[#1a1a2e] border-[#2d2d44] max-w-2xl max-h-[90vh] overflow-y-auto">
          {selectedReport && (
            <>
              <DialogHeader>
                <div className="flex items-center justify-between">
                  <DialogTitle className="text-white">{selectedReport.subject}</DialogTitle>
                  <Badge className={`${getStatusInfo(selectedReport.status).color} text-white`}>
                    {getStatusInfo(selectedReport.status).label}
                  </Badge>
                </div>
                <DialogDescription className="text-gray-400">
                  Report #{selectedReport.id.slice(0, 8)}
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-4 py-4">
                {/* Report Info */}
                <div className="grid grid-cols-2 gap-4 p-4 bg-[#252540] rounded-lg">
                  <div>
                    <p className="text-xs text-gray-500 uppercase">Reporter</p>
                    <p className="text-white">{selectedReport.reporter_name}</p>
                    <p className="text-sm text-gray-400">{selectedReport.reporter_email}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 uppercase">Submitted</p>
                    <p className="text-white">{formatDate(selectedReport.created_at)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 uppercase">Type</p>
                    <p className="text-white">{getTypeInfo(selectedReport.report_type).label}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 uppercase">Priority</p>
                    <Badge className={`${getPriorityInfo(selectedReport.priority).color} text-white`}>
                      {getPriorityInfo(selectedReport.priority).label}
                    </Badge>
                  </div>
                </div>

                {/* Reported User (for spam reports) */}
                {selectedReport.reported_user_email && (
                  <div className="p-4 bg-orange-500/10 border border-orange-500/30 rounded-lg">
                    <p className="text-xs text-orange-400 uppercase mb-1">Reported User</p>
                    <p className="text-white font-mono">{selectedReport.reported_user_email}</p>
                  </div>
                )}

                {/* Description */}
                <div className="space-y-2">
                  <p className="text-xs text-gray-500 uppercase">Description</p>
                  <div className="p-4 bg-[#252540] rounded-lg">
                    <p className="text-gray-300 whitespace-pre-wrap">{selectedReport.description}</p>
                  </div>
                </div>

                {/* Screenshot */}
                {selectedReport.screenshot_data && (
                  <div className="space-y-2">
                    <p className="text-xs text-gray-500 uppercase">Screenshot</p>
                    <img 
                      src={`data:image/png;base64,${selectedReport.screenshot_data}`}
                      alt="Report screenshot"
                      className="max-w-full rounded-lg border border-[#2d2d44]"
                    />
                  </div>
                )}

                {/* Comments */}
                <div className="space-y-2">
                  <p className="text-xs text-gray-500 uppercase">Comments & Activity</p>
                  <ScrollArea className="h-[200px] p-4 bg-[#252540] rounded-lg">
                    {selectedReport.comments?.length === 0 ? (
                      <p className="text-gray-500 text-center py-4">No comments yet</p>
                    ) : (
                      <div className="space-y-3">
                        {selectedReport.comments?.map((comment) => (
                          <div 
                            key={comment.id} 
                            className={`p-3 rounded-lg ${
                              comment.is_reply 
                                ? 'bg-[#6D4AFF]/10 border border-[#6D4AFF]/30' 
                                : comment.is_internal 
                                  ? 'bg-yellow-500/10 border border-yellow-500/30'
                                  : 'bg-[#1a1a2e]'
                            }`}
                          >
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-xs font-medium text-white">
                                {comment.author_name || comment.author_email}
                              </span>
                              {comment.is_internal && (
                                <Badge className="bg-yellow-500/20 text-yellow-400 text-[10px]">
                                  Internal
                                </Badge>
                              )}
                              {comment.is_reply && (
                                <Badge className="bg-[#6D4AFF]/20 text-[#6D4AFF] text-[10px]">
                                  Reply to User
                                </Badge>
                              )}
                              <span className="text-xs text-gray-500 ml-auto">
                                {formatDate(comment.created_at)}
                              </span>
                            </div>
                            <p className="text-sm text-gray-300">{comment.comment}</p>
                          </div>
                        ))}
                      </div>
                    )}
                  </ScrollArea>
                </div>

                {/* Add Comment */}
                <div className="space-y-2">
                  <div className="flex items-center gap-4">
                    <Label className="text-gray-300 flex-shrink-0">Add Note</Label>
                    <label className="flex items-center gap-2 text-sm text-gray-400 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={isInternalComment}
                        onChange={(e) => setIsInternalComment(e.target.checked)}
                        className="rounded border-gray-600"
                      />
                      Internal only (not visible to user)
                    </label>
                  </div>
                  <div className="flex gap-2">
                    <Input
                      value={newComment}
                      onChange={(e) => setNewComment(e.target.value)}
                      placeholder="Add a comment..."
                      className="bg-[#252540] border-[#2d2d44] text-white"
                    />
                    <Button
                      onClick={handleAddComment}
                      disabled={!newComment.trim() || submitting}
                      className="bg-[#6D4AFF] hover:bg-[#5B3FD9]"
                    >
                      Add
                    </Button>
                  </div>
                </div>
              </div>

              <DialogFooter className="flex-col sm:flex-row gap-2">
                <Button 
                  variant="ghost" 
                  onClick={() => setViewDialogOpen(false)}
                  className="text-gray-400"
                >
                  Close
                </Button>
                <Button
                  onClick={() => setReplyDialogOpen(true)}
                  className="bg-green-600 hover:bg-green-700"
                >
                  <Send className="w-4 h-4 mr-2" />
                  Reply to User
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Reply Dialog */}
      <Dialog open={replyDialogOpen} onOpenChange={setReplyDialogOpen}>
        <DialogContent className="bg-[#1a1a2e] border-[#2d2d44]">
          <DialogHeader>
            <DialogTitle className="text-white">Reply to User</DialogTitle>
            <DialogDescription className="text-gray-400">
              Send a response to {selectedReport?.reporter_email}
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <Textarea
              value={replyMessage}
              onChange={(e) => setReplyMessage(e.target.value)}
              placeholder="Type your response..."
              className="bg-[#252540] border-[#2d2d44] text-white min-h-[150px]"
            />
          </div>
          
          <DialogFooter>
            <Button 
              variant="ghost" 
              onClick={() => setReplyDialogOpen(false)}
              className="text-gray-400"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSendReply}
              disabled={!replyMessage.trim() || submitting}
              className="bg-green-600 hover:bg-green-700"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Send Reply
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
